import denormalizeResponse from '@/utils/denormalizeResponse'
import router from '@/router'
import validate from '@/services/rostering/validate'
import prepareEntries from '@/services/rostering/prepareEntries'
import api from '@/services/api'
import _ from 'lodash'
import levenshtein from 'js-levenshtein'

async function requestSelf(query) {
    const data = await api.get('/api/v2/me', { query })

	return denormalizeResponse(data).root
}

async function requestResource(type, id, query) {
    const data = await api.get(`/api/v2/resources/${type}/${id}`, { query })

    return denormalizeResponse(data).root
}

async function requestAccounts(email, password, query = {}) {
    const data = await api.post('/api/v2/auth/accounts', { email, password, query })

    return denormalizeResponse(data).root
}

async function requestLogin({ email, password, accountId }) {
    const data = await api.post('/api/v2/auth/login', {
        email,
        password,
        account: {
            type: 'clubStaffs',
            id: accountId,
        }
    })

    return data
}

export async function retrieveEventId({ commit }, token) {
    try {
        const { eventId } = await api.get('/api/v1/rostering/event-id', { token })
        commit('retrieveEventIdSuccess', { eventId })
    } catch (e) {
        commit('retrieveEventIdError')
    }
}

export async function retrieveEvent({ commit }, eventId) {
    commit('retrieveEventStart', { eventId })

    try {
        const event = await requestResource('events', eventId, {
			include: {
                name: true,
                locationCity: true,
                locationState: true,
                startDate: true,
                endDate: true,
                images: {
                    url: true,
                    isProfile: true,
                },
                sport: {
                    name: true,
                    positions: true,
                },
                gender: true,
				teams: {
					name: true,
					division: {
						name: true,
                    },
                    isRostered: true,
				},
			},
		})

        commit('retrieveEventSuccess', { eventId, event })
    } catch (error) {
        console.error(error)
        commit('retrieveEventError', { eventId, error })
    }
}

export async function retrieveOwnInfo({ state, commit }) {
    commit('retrieveOwnInfoStart')

    try {
        const account = await requestSelf({
			include: {
				organization: {
					name: true,

					sport: {
						name: true,
					},

					gender: true,

					teams: {
						name: true,
						athleteAffiliations: {
							athlete: { id: true },
							jerseyNumber: true,
						},
						profileImage: {
							url: true,
						},
					},

					athletes: {
						firstName: true,
						lastName: true,
						classYear: true,
						emailAddress: true,
						guardians: {
							emailAddress: true,
						},
						phoneNumber: true,
						jerseyNumber: true,
						positions: true,
						images: {
							url: true,
							isProfile: true,
						},
						clubAffiliations: {
							organization: { id: true },
							jerseyNumber: true,
						},
						highSchoolAffiliations: {
							organization: { id: true },
							jerseyNumber: true,
						},
						teamAffiliations: {
							team: {
								name: true,
								organization: { id: true },
							},
							jerseyNumber: true,
						},
					},
				},
			},
		})

        commit('retrieveOwnInfoSuccess', { account })
    } catch (error) {
        console.error(error)
        commit('retrieveOwnInfoError', { error })
    }
}

export async function openWorkspace({ commit }) {
    commit('openWorkspace')
}

export async function closeWorkspace({ commit }) {
    commit('closeWorkspace')
}

export async function uploadCsv({ commit, dispatch }, { csv }) {
    commit('setCsv', csv)
    dispatch('guessMappings', { csv })
}

export async function clearCsv({ commit }) {
    commit('clearCsv')
}

let nextValidationNonce = 0

export async function validateEntries({ state, commit }, { entries } = {}) {
    entries = entries || state.workspace.entries // TODO

    const { eventId } = state
    const { token } = router.currentRoute.params
    const nonce = nextValidationNonce++

    commit('validateEntriesStart', { nonce })

    const errors = await validate(entries, { eventId, token })

    commit('validateEntriesSuccess', { nonce, errors })
}

export async function matchColumn({ state, commit }, { fieldName, columnName }) {
    if (columnName) {
        commit('addFieldMapping', {
            fieldName,
            columnName,
        })
    } else {
        commit('removeMappingByField', {
			fieldName,
		})
    }
}

export async function clearMappings({ commit }) {
    commit('clearMappings')
}

export async function overwriteEntries({ state, commit, dispatch }, { entries }) {
	commit('overwriteEntries', {
		entries,
    })
    
    dispatch('validateEntries')
}

export async function addEntry({ commit, dispatch }, entry) {
    commit('addEntry', entry)
    dispatch('validateEntries')
}

export async function addEntries({ commit, dispatch }, entries) {
	commit('addEntries', entries)
	dispatch('validateEntries')
}

export async function deleteEntry({ commit, dispatch }, entry) {
    commit('deleteEntry', entry)
    dispatch('validateEntries')
}

export async function goToTeams({ state, commit }) {
    router.push({ name: 'Rostering/Event/Teams/List' })
}

export async function register({ state, commit }, { firstName, lastName, email, phone, roleId }) {
    commit('submitRegistrationStart')

    try {
        const eventId = state.eventId
        const token = router.currentRoute.params.token

        await api.post(`/api/v1/rostering/${eventId}/${token}/register`, {
            firstName,
            lastName,
            email,
            phone,
            roleId,
        })

        commit('submitRegistrationSuccess', {
            identity: {
                firstName,
                lastName,
                email,
                phone,
                roleId,
            }
        })
    } catch (e) {
        console.error(e)
        const errors = extractErrors(e)
        commit('submitRegistrationError', { errors })
    }
}

export async function retrieveAccounts({ state, commit, dispatch }, { email, password }) {
    commit('retrieveAccountsStart')

    try {
        const allAccounts = await requestAccounts(email, password, {
            include: {
                firstName: true,
                lastName: true,
                organization: {
                    name: true,
                    gender: true,
                    sport: {
                        name: true,
                    }
                },
                position: true,
                images: {
                    url: true,
                    isProfile: true,
                },
            }
        })

        const staffAccounts = allAccounts.filter(account => {
            return account.type === 'clubStaffs'
                && account.organization
                && account.organization.gender == state.event.gender
                && account.organization.sport.name == state.event.sport.name
        })

        if (staffAccounts.length) {
            commit('retrieveAccountsSuccess', { accounts: staffAccounts })

            if (staffAccounts.length === 1) {
                // Go ahead and log in, no need for disambiguation.
                dispatch('chooseAccount', { email, password, accountId: staffAccounts[0].id })
            }
        } else {
            commit('retrieveAccountsError', {
                errors: [{
                    message: 'Incorrect email or password.'
                }]
            })
        }
    } catch (e) {
        console.error(e)
        const errors = extractErrors(e)
        commit('retrieveAccountsError', { errors })
    }
}

export async function clearAccountRetrieval({ commit }) {
    commit('clearAccountRetrieval')
}

export async function clearLoginSubmission({ commit }) {
    commit('clearLoginSubmission')
}

export async function clearRegistrationSubmission({ commit }) {
	commit('clearRegistrationSubmission')
}

export async function chooseAccount({ dispatch, commit }, { email, password, accountId }) {
    commit('chooseAccountStart', { accountId })

    try {
        const { token } = await requestLogin({ email, password, accountId })

        applyAuth({ accountId, token })

        commit('chooseAccountSuccess', { accountId, token })

        dispatch('retrieveOwnInfo')
        dispatch('goToTeams')
    } catch (e) {
        console.error(e)
        const errors = extractErrors(e)
        commit('chooseAccountError', { errors })
    }
}

export async function switchAccount({ state, dispatch, commit }, { accountId }) {
    commit('switchAccountStart', { accountId })

    try {
        const { token: newToken } = await api.post('/api/v2/auth/switch', { accountId })

        applyAuth({ accountId, token: newToken })

        commit('switchAccountSuccess', { accountId, token: newToken })

        dispatch('retrieveOwnInfo')
    } catch (e) {
        console.error(e)
        const errors = extractErrors(e)
        commit('switchAccountError', { errors })
    }
}

export async function restoreAuth({ state, commit, dispatch }) {
    const json = localStorage.getItem('integratedIdentity')

    if (json) {
        const { accountId, token } = JSON.parse(json)

        applyAuth({ accountId, token })

        commit('setIntegratedIdentity', { accountId, token })

        dispatch('retrieveOwnInfo')
    }
}

export async function clearIdentity({ commit }) {
    clearAuth()
    commit('clearIdentity')
}

function applyAuth({ accountId, token }) {
    api.setToken(token)
    localStorage.setItem('integratedIdentity', JSON.stringify({ accountId, token }))
}

function clearAuth() {
    api.setToken(null)
    localStorage.removeItem('integratedIdentity')
}

export function guessMappings({ state, commit }, { csv }) {
    const mappings = csv.headers
        .map(columnName => {
            const field = findProbableMatchField(state.fields, columnName)

            if (field) {
                return { fieldName: field.name, columnName }
            }
        })
        .filter(Boolean)

    mappings.forEach(mapping => commit('addFieldMapping', mapping))
}

function findProbableMatchField(fields, columnName) {
    const distances = fields.map(field => getBestDistance(field, columnName))
    const threshold = 2

    const best = _(fields)
        .zip(distances)
        .filter(([ field, distance ]) => distance < threshold)
        .sortBy(([ field, distance ]) => distance)
        .map(([ field, distance ]) => field)
        .first()

    return best
}

function getBestDistance(field, columnName) {
    const aliases = _.uniq([ field.label, ...field.aliases || [] ])

    return _(aliases)
        .map(alias => getEditDistance(alias, columnName))
        .min()
}

function getEditDistance(a, b) {
    const normalize = s => s.replace(/[^a-z]/i, '').toLowerCase()

    return levenshtein(
        normalize(a),
        normalize(b)
    )
}

export async function submitRoster({ state, commit }, { teamId }) {
    commit('submitRosterStart', { teamId })

    try {
        const identity = state.auth.nonIntegratedIdentity
        const eventId = state.eventId
        const token = router.currentRoute.params.token
        const entries = state.workspace.entries

        await api.post(`/api/v1/rostering/${eventId}/${token}/${teamId}/submit`, {
            identity: identity,
            athletes: prepareEntries(entries)
        })

        commit('submitRosterSuccess', { teamId })
    } catch (e) {
        console.error(e)
        const errors = extractErrors(e)
        commit('submitRosterError', { teamId, errors })
    }
}

function extractErrors(error) {
    return error.response
        ? extractResponseErrors(error.response)
        : [{ message: error.message }]
}

function extractResponseErrors({ errors, message }) {
    if (errors) {
        return _(errors)
            .toPairs()
            .map(([ fieldName, messages ]) => messages.map(message => ({ message, fieldName })))
            .flatten()
            .value()
    } else {
        return [{ message }]
    }
}